import { Component, ChangeDetectionStrategy } from '@angular/core';
import { PDF_ZOOM_LEVELS } from '@app/shared/constants';

export interface FaqItem {
  question: string;
  answer: string;
  servicenowUrl?: string;
}

export type MediaType = 'pdf' | 'video';

export interface MediaItem {
  lang: 'en' | 'no';
  label: string;
  url: string;
}

export interface MediaViewer {
  type: MediaType;
  title: string;
  items: MediaItem[];
  activeLang: 'en' | 'no';
}

@Component({
  selector: 'app-support',
  templateUrl: './support.component.html',
  styleUrls: ['./support.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SupportComponent {
  readonly pdfZoomLevels = PDF_ZOOM_LEVELS;
  readonly servicenowUrl =
    'https://servicenow.akersolutions.com/esc?id=sc_cat_item&sys_id=98a848aa87e76110664862040cbb35a5';

  mediaViewer: MediaViewer | null = null;

  private readonly pdfItems: MediaItem[] = [
    { lang: 'en', label: 'English', url: 'assets/user-guides/pdfs/Digital Work Order User Guide ENG.pdf' },
    { lang: 'no', label: 'Norwegian', url: 'assets/user-guides/pdfs/Digital Work Order Brukerveileder NOR.pdf' },
  ];

  private readonly videoItems: MediaItem[] = [
    { lang: 'en', label: 'English', url: 'assets/user-guides/videos/Digital Work Order 1.3.0 English.mp4' },
    { lang: 'no', label: 'Norwegian', url: 'assets/user-guides/videos/Digital Work Order 1.3.0 Norsk.mp4' },
  ];

  openMedia(type: MediaType, lang: 'en' | 'no'): void {
    const items = type === 'pdf' ? this.pdfItems : this.videoItems;
    const title = type === 'pdf' ? 'User Guide (PDF)' : 'User Guide (Video)';
    if (this.mediaViewer?.type === type && this.mediaViewer.activeLang === lang) {
      this.mediaViewer = null;
    } else {
      this.mediaViewer = { type, title, items, activeLang: lang };
    }
  }

  switchLang(lang: 'en' | 'no'): void {
    if (this.mediaViewer) {
      this.mediaViewer = { ...this.mediaViewer, activeLang: lang };
    }
  }

  closeMedia(): void {
    this.mediaViewer = null;
  }

  get activeMediaItem(): MediaItem | undefined {
    return this.mediaViewer?.items.find((i) => i.lang === this.mediaViewer!.activeLang);
  }

  readonly faqs: FaqItem[] = [
    {
      question: 'I cannot access Digital Work Order. What should I do?',
      answer:
        'Ensure you are using the correct credentials and have been granted access to the project. ' +
        'AKSO employees should raise a ServiceNow request. External users should contact the project contact person.',
      servicenowUrl: 'https://servicenow.akersolutions.com/esc?id=sc_cat_item&sys_id=98a848aa87e76110664862040cbb35a5',
    },
    {
      question: 'I am missing work orders that I expect to see.',
      answer:
        'Work orders are filtered by your assigned project and location. ' +
        'Verify your profile settings and check with your project contact person that your access has been correctly configured.',
    },
    {
      question: 'The application is slow or not loading correctly.',
      answer:
        'Try refreshing the page or clearing your browser cache. ' +
        'If the issue persists, AKSO employees should raise a ServiceNow request.',
      servicenowUrl: 'https://servicenow.akersolutions.com/esc?id=sc_cat_item&sys_id=98a848aa87e76110664862040cbb35a5',
    },
    {
      question: 'How do I report a defect or request a new feature?',
      answer:
        'AKSO employees should raise a ServiceNow ticket describing the issue or feature request in detail, ' +
        'including screenshots if possible.',
      servicenowUrl: 'https://servicenow.akersolutions.com/esc?id=sc_cat_item&sys_id=98a848aa87e76110664862040cbb35a5',
    },
    {
      question: 'Who is the project contact person for my project?',
      answer:
        'Contact your project manager or site administrator to identify the project contact person. ' +
        'Contact persons are responsible for managing user access and first-line support for external users.',
    },
    {
      question: 'Where can I find user guides for Digital Work Order?',
      answer:
        'User guides are available in Norwegian and English. See the "User Guides" section above for download links.',
    },
  ];
}
